<template lang="pug">
	div.page-login
		.page-login__bg(aria-hidden="true")
		.page-login__theme
			ThemeToggle
		.page-login__card
			.page-login__brand
				.page-login__logo
					img(alt="Logo", :src="logoPath")
				h1.page-login__title Partners
				p.page-login__subtitle Sign in to your dashboard
			form.page-login__form(@submit.prevent="submit")
				.page-login__field
					label.page-login__label(for="login-email") Email
					.page-login__input-wrap
						i.la.la-envelope.page-login__input-icon(aria-hidden="true")
						input#login-email.page-login__input(
							type="email",
							v-model="email",
							placeholder="you@company.com",
							autocomplete="email",
							required
						)
				.page-login__field
					label.page-login__label(for="login-password") Password
					.page-login__input-wrap
						i.la.la-lock.page-login__input-icon(aria-hidden="true")
						input#login-password.page-login__input.page-login__input--password(
							:type="showPassword ? 'text' : 'password'",
							v-model="password",
							placeholder="Enter your password",
							autocomplete="current-password",
							required
						)
						button.page-login__password-toggle(
							type="button",
							:aria-label="showPassword ? 'Hide password' : 'Show password'",
							:aria-pressed="showPassword",
							@click="showPassword = !showPassword"
						)
							i.la(:class="showPassword ? 'la-eye-slash' : 'la-eye'", aria-hidden="true")
				.page-login__error(v-if="errorText", role="alert") {{ errorText }}
				button.page-login__submit(type="submit", :disabled="busy")
					template(v-if="busy")
						OvBusySpinner(:size="18")
						span Signing in…
					template(v-else)
						span Sign in
						i.la.la-arrow-right(aria-hidden="true")
</template>

<style lang="scss">
.page-login {
	--login-input-bg: color-mix(in srgb, var(--ds-color-bg) 72%, var(--ds-color-surface));
	--login-glow: color-mix(in srgb, var(--ds-color-primary) 14%, transparent);
	--login-input-padding-start: calc(var(--ds-space-4) + var(--ds-icon-size-md) + var(--ds-space-2));

	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 100vh;
	min-height: 100dvh;
	padding: var(--ds-space-6) var(--ds-space-4);
	overflow: hidden;

	&__bg {
		position: absolute;
		inset: 0;
		background:
			radial-gradient(ellipse 80% 60% at 50% -10%, var(--login-glow), transparent 70%),
			radial-gradient(ellipse 50% 40% at 100% 100%, var(--login-glow), transparent 60%),
			var(--ds-color-bg);
		pointer-events: none;
	}

	&__theme {
		position: absolute;
		top: var(--ds-space-4);
		right: var(--ds-space-4);
		z-index: 2;

		.btn-theme-toggle {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: var(--ds-icon-btn-size);
			height: var(--ds-icon-btn-size);
			font-size: var(--ds-icon-size-md);
			color: var(--ds-color-text-muted);
			background: color-mix(in srgb, var(--ds-color-surface) 88%, transparent);
			backdrop-filter: blur(var(--ds-blur-sm));
			border: var(--ds-border-width) solid var(--ds-color-border-light);
			border-radius: var(--ds-radius-full);
			cursor: pointer;
			transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.2s;

			&:hover {
				color: var(--ds-color-text);
				background: var(--ds-color-surface);
				border-color: var(--ds-color-border);
				transform: scale(1.04);
			}
		}
	}

	&__card {
		position: relative;
		z-index: 1;
		width: 100%;
		max-width: var(--ds-content-narrow);
		padding: var(--ds-space-6) var(--ds-space-5);
		background: color-mix(in srgb, var(--ds-color-surface) 96%, transparent);
		backdrop-filter: blur(var(--ds-blur-md));
		border: var(--ds-border-width) solid color-mix(in srgb, var(--ds-color-border) 60%, transparent);
		border-radius: var(--ds-radius-xl);
		box-shadow: var(--ds-shadow-lg);
	}

	&__brand {
		text-align: center;
		margin-bottom: var(--ds-space-5);
	}

	&__logo {
		margin-bottom: var(--ds-space-4);

		img {
			display: block;
			max-width: var(--ds-logo-max-width);
			max-height: var(--ds-logo-max-height);
			width: auto;
			height: auto;
			margin: 0 auto;
			object-fit: contain;
		}
	}

	&__title {
		margin: 0 0 var(--ds-space-1);
		font-size: var(--ds-font-size-2xl);
		font-weight: var(--ds-font-weight-semibold);
		letter-spacing: -0.02em;
		color: var(--ds-color-text);
	}

	&__subtitle {
		margin: 0;
		font-size: var(--ds-font-size-sm);
		color: var(--ds-color-text-muted);
	}

	&__form {
		text-align: left;
	}

	&__field {
		margin-bottom: var(--ds-space-4);
	}

	&__label {
		display: block;
		margin-bottom: var(--ds-space-2);
		font-size: var(--ds-font-size-xs);
		font-weight: var(--ds-font-weight-medium);
		color: var(--ds-color-text-muted);
		letter-spacing: 0.01em;
	}

	&__input-wrap {
		position: relative;
	}

	&__input-icon {
		position: absolute;
		left: var(--ds-space-4);
		top: 50%;
		transform: translateY(-50%);
		font-size: var(--ds-icon-size-md);
		color: var(--ds-color-text-subtle);
		pointer-events: none;
		transition: color 0.2s;
	}

	&__input {
		display: block;
		width: 100%;
		height: var(--ds-control-height-lg);
		padding: 0 var(--ds-space-4) 0 var(--login-input-padding-start);
		font-family: inherit;
		font-size: var(--ds-font-size-sm);
		color: var(--ds-color-text);
		background: var(--login-input-bg);
		border: var(--ds-border-width-emphasis) solid var(--ds-color-border);
		border-radius: var(--ds-radius);
		transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;

		&::placeholder {
			color: var(--ds-color-text-subtle);
		}

		&:hover:not(:focus) {
			border-color: color-mix(in srgb, var(--ds-color-border) 50%, var(--ds-color-text-subtle));
		}

		&:focus {
			outline: none;
			border-color: var(--ds-color-primary);
			box-shadow: var(--ds-focus-ring);
			background: var(--ds-color-surface);
		}
	}

	&__input-wrap:focus-within .page-login__input-icon {
		color: var(--ds-color-primary);
	}

	&__input--password {
		padding-right: calc(var(--ds-space-4) + var(--ds-icon-size-md) + var(--ds-space-2));
	}

	&__password-toggle {
		position: absolute;
		right: var(--ds-space-2);
		top: 50%;
		transform: translateY(-50%);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: var(--ds-icon-btn-size);
		height: var(--ds-icon-btn-size);
		padding: 0;
		font-size: var(--ds-icon-size-md);
		color: var(--ds-color-text-subtle);
		background: transparent;
		border: none;
		border-radius: var(--ds-radius);
		cursor: pointer;
		transition: color 0.2s, background 0.2s;

		&:hover {
			color: var(--ds-color-text);
			background: color-mix(in srgb, var(--ds-color-text) 6%, transparent);
		}

		&:focus-visible {
			outline: none;
			box-shadow: var(--ds-focus-ring);
		}
	}

	&__input-wrap:focus-within .page-login__password-toggle {
		color: var(--ds-color-text-muted);
	}

	&__error {
		margin-bottom: var(--ds-space-4);
		padding: var(--ds-space-3) var(--ds-space-4);
		font-size: var(--ds-font-size-sm);
		color: #b91c1c;
		background: var(--ds-color-danger-subtle);
		border: var(--ds-border-width) solid color-mix(in srgb, #ef4444 25%, transparent);
		border-radius: var(--ds-radius);
	}

	&__submit {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--ds-space-2);
		width: 100%;
		height: var(--ds-control-height-lg);
		margin-top: var(--ds-space-2);
		padding: 0 var(--ds-space-5);
		font-family: inherit;
		font-size: var(--ds-font-size-sm);
		font-weight: var(--ds-font-weight-semibold);
		color: var(--ds-color-primary-text);
		background: linear-gradient(135deg, #ffc96a 0%, var(--ds-color-primary) 48%, #f09020 100%);
		border: none;
		border-radius: var(--ds-radius-full);
		cursor: pointer;
		box-shadow: var(--ds-shadow-primary);
		transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;

		.la-arrow-right {
			font-size: var(--ds-icon-size-md);
			transition: transform 0.2s;
		}

		&:hover:not(:disabled) {
			transform: translateY(calc(var(--ds-border-width) * -1));
			box-shadow: 0 clamp(0.375rem, 0.3rem + 0.35vw, 0.5rem) clamp(1.25rem, 1rem + 0.85vw, 1.5rem) color-mix(in srgb, var(--ds-color-primary) 45%, transparent);

			.la-arrow-right {
				transform: translateX(var(--ds-space-1));
			}
		}

		&:active:not(:disabled) {
			transform: translateY(0);
		}

		&:disabled {
			opacity: 0.65;
			cursor: not-allowed;
		}
	}
}

[data-color-mode="dark"] .page-login {
	--login-input-bg: color-mix(in srgb, var(--ds-color-bg) 55%, var(--ds-color-surface));
	--login-glow: color-mix(in srgb, var(--ds-color-primary) 10%, transparent);

	&__error {
		color: #fca5a5;
	}
}

[data-color-mode="medium"] .page-login {
	--login-glow: color-mix(in srgb, var(--ds-color-primary) 10%, transparent);
}
</style>

<script>
import axios from "axios";
import { mapActions } from "pinia";
import { useAuthStore } from "@/stores/auth";
import http, { setAuthToken } from "@/api/http";
import ThemeToggle from "@/components/ThemeToggle.vue";

const logos = import.meta.glob("../assets/*-logo.png", { eager: true, import: "default" });

export default {
	components: { ThemeToggle },
	data() {
		return {
			errorText: "",
			busy: false,
			email: "",
			password: "",
			showPassword: false,
			envName: import.meta.env.VUE_APP_ENV_NAME,
		};
	},
	computed: {
		logoPath() {
			const key = `../assets/${this.envName}-logo.png`;
			return logos[key] || logos["../assets/ovalio-media-logo.png"];
		},
	},
	methods: {
		...mapActions(useAuthStore, ["setUser"]),
		submit() {
			if (this.busy) return;

			this.errorText = "";
			this.busy = true;

			const loginUrl = import.meta.env.VUE_APP_API_BASE_URL + "api/auth/loginPartners";
			axios.post(loginUrl, {
				email: this.email,
				password: this.password,
			}).then(async (response) => {
				const token = response.data.data.token;
				setAuthToken(token);
				try {
					const current = await http.get("user/getCurrent");
					if (current.data.err === 0) {
						this.setUser({
							user: {
								...current.data.data.user,
								config: current.data.data.config,
							},
							token,
						});
					} else {
						this.setUser({ user: response.data.data.user, token });
					}
				} catch {
					this.setUser({ user: response.data.data.user, token });
				}
				this.$router.push("/app");
			}).catch((response) => {
				this.busy = false;
				if (response.response?.data?.errdesc) {
					this.errorText = response.response.data.errdesc;
				} else {
					this.errorText = "There has been an error";
				}
			});
		},
	},
};
</script>
